import React, { useState, useEffect, useRef } from 'react';
import { Box, Alert, AlertTitle, Fade, Grow, Paper, Typography, Button } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { ExtendedAnalysisResponse } from './utils/statusMappingUtils';
import ResultsLoadingSkeleton from './ResultsLoadingSkeleton';
import KeywordResultsVisualization from './KeywordResultsVisualization';
import JobStatusPanel from './JobStatusPanel';

// Define the job status state machine
type JobPhase = 
  | 'queued'
  | 'processing'
  | 'completing'
  | 'preparing-results'
  | 'loading-data'
  | 'processing-file'
  | 'results-ready'
  | 'failed'
  | 'cancelled';

interface JobStatusTransitionProps {
  status: ExtendedAnalysisResponse;
  jobId?: string;
  keywordsJsonContent?: string | null;
  isLoadingJson?: boolean;
  isSavingToSnowflake?: boolean;
  snowflakeSaveSuccess?: boolean;
  snowflakeSaveError?: string | null;
  onJsonContentLoad?: (content: string) => void;
  fileTooLarge?: boolean;
  keywordCount?: number | null;
}

interface TransitionState {
  currentPhase: JobPhase;
  progress: number;
  message: string;
  showSkeleton: boolean;
  showResults: boolean;
  transitionKey: string;
}

const JobStatusTransition: React.FC<JobStatusTransitionProps> = ({
  status,
  jobId,
  keywordsJsonContent,
  isLoadingJson,
  isSavingToSnowflake,
  snowflakeSaveSuccess,
  snowflakeSaveError,
  onJsonContentLoad,
  fileTooLarge,
  keywordCount
}) => {
  const [transitionState, setTransitionState] = useState<TransitionState>({
    currentPhase: 'queued',
    progress: 0,
    message: '',
    showSkeleton: false,
    showResults: false,
    transitionKey: Date.now().toString()
  });

  const previousStatusRef = useRef<string>('');
  const transitionTimeoutRef = useRef<NodeJS.Timeout>();
  const hasResultsUrlRef = useRef<boolean>(false);

  // Determine the current phase based on status and available data
  const determinePhase = (
    currentStatus: string, 
    hasOutputLinks: boolean, 
    hasJsonContent: boolean,
    isJsonLoading: boolean
  ): JobPhase => {
    switch (currentStatus) {
      case 'queued':
        return 'queued';
      case 'processing':
        return 'processing';
      case 'completed':
        if (!hasOutputLinks) {
          return 'completing';
        }
        if (hasOutputLinks && !hasJsonContent && isJsonLoading) {
          return 'loading-data';
        }
        if (hasOutputLinks && !hasJsonContent && !isJsonLoading) {
          return 'preparing-results';
        }
        if (hasJsonContent && isJsonLoading) {
          return 'processing-file';
        }
        if (hasJsonContent && !isJsonLoading) {
          return 'results-ready';
        }
        return 'preparing-results';
      case 'failed':
        return 'failed';
      case 'cancelled':
        return 'cancelled';
      default:
        return 'processing';
    }
  };

  // Calculate progress based on phase and status
  const calculateProgress = (phase: JobPhase, status: ExtendedAnalysisResponse): number => {
    switch (phase) {
      case 'queued':
        return 0;
      case 'processing':
        if (status.total_items && status.processed_items) {
          return (status.processed_items / status.total_items) * 85; // Leave room for completion phases
        }
        return 45; // Default processing progress
      case 'completing':
        return 90;
      case 'preparing-results':
        return 95;
      case 'loading-data':
        return 97;
      case 'processing-file':
        return 99;
      case 'results-ready':
        return 100;
      case 'failed':
      case 'cancelled':
        return 0;
      default:
        return 0;
    }
  };

  // Get appropriate message for each phase
  const getPhaseMessage = (phase: JobPhase, status: ExtendedAnalysisResponse): string => {
    switch (phase) {
      case 'queued':
        return 'Your job is queued and will start processing shortly...';
      case 'processing':
        const processed = status.processed_items || 0;
        const total = status.total_items || 0;
        if (total > 0) {
          return `Processing keywords: ${processed} of ${total} completed`;
        }
        return 'Processing your keywords...';
      case 'completing':
        return 'Finalizing keyword classification results...';
      case 'preparing-results':
        return 'Generating output files and preparing download links...';
      case 'loading-data':
        return 'Retrieving your classified keyword data...';
      case 'processing-file':
        return 'Processing results file for visualization...';
      case 'results-ready':
        return 'Results are ready!';
      case 'failed':
        return 'Job processing failed. Please try again or contact support.';
      case 'cancelled':
        return 'Job was cancelled.';
      default:
        return 'Processing...';
    }
  };

  // Main effect to manage state transitions
  useEffect(() => {
    const hasOutputLinks = Boolean(
      status?.output_links?.['results.json'] || 
      status?.output_links?.categorized_keywords
    );
    const hasJsonContent = Boolean(keywordsJsonContent);

    // Track if results URL becomes available
    if (hasOutputLinks && !hasResultsUrlRef.current) {
      hasResultsUrlRef.current = true;
    }

    const hasContentForPhase = hasJsonContent || fileTooLarge;
    
    const newPhase = determinePhase(
      status.status, 
      hasOutputLinks, 
      hasContentForPhase,
      Boolean(isLoadingJson)
    );
    
    const progress = calculateProgress(newPhase, status);
    const message = getPhaseMessage(newPhase, status);
    
    // Determine what to show based on phase
    const showSkeleton = [
      'completing', 
      'preparing-results', 
      'loading-data', 
      'processing-file'
    ].includes(newPhase);
    
    const showResults = newPhase === 'results-ready';

    // Only update state if phase actually changed to avoid unnecessary re-renders
    setTransitionState(prev => {
      if (prev.currentPhase !== newPhase || 
          Math.abs(prev.progress - progress) > 1 ||
          prev.showSkeleton !== showSkeleton ||
          prev.showResults !== showResults) {
        
        // Generate new transition key for smooth animations
        const transitionKey = `${newPhase}-${Date.now()}`;
        
        return {
          currentPhase: newPhase,
          progress,
          message,
          showSkeleton,
          showResults,
          transitionKey
        };
      }
      return prev;
    });

    // Clear any existing timeout
    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current);
    }

    // Handle automatic progression for certain phases
    if (newPhase === 'completing' && status.status === 'completed' && hasOutputLinks) {
      transitionTimeoutRef.current = setTimeout(() => {
        // Force transition to preparing-results if we have output links
        setTransitionState(prev => ({
          ...prev,
          currentPhase: 'preparing-results',
          progress: 95,
          message: 'Generating output files and preparing download links...',
          transitionKey: `preparing-results-${Date.now()}`
        }));
      }, 1500); // Brief pause to show completion
    }

    previousStatusRef.current = status.status;
  }, [
    status.status, 
    status.output_links, 
    status.processed_items, 
    status.total_items,
    keywordsJsonContent, 
    isLoadingJson,
    fileTooLarge  // ✅ CRITICAL: Re-run when fileTooLarge changes
  ]);

  // Cleanup effect
  useEffect(() => {
    return () => {
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }
    };
  }, []);

  // Render the appropriate component based on current phase
  const renderContent = () => {
    const { currentPhase, progress, message, showSkeleton, showResults } = transitionState;

    // Show message if file is too large for visualization
    if (showResults && fileTooLarge) {
      const formattedCount = keywordCount ? keywordCount.toLocaleString() : 'Unknown';
      return (
        <Grow in={true} timeout={600}>
          <Box>
            <Alert severity="success" sx={{ mb: 3 }}>
              <AlertTitle>✅ Job Completed Successfully - Dataset Too Large for Visualization</AlertTitle>
              <strong>Your analysis is complete!</strong> The results contain {formattedCount} keywords, which exceeds the 10,000 keyword limit for browser visualization to prevent performance issues.
              <br /><br />
              ✅ Results have been successfully saved to Snowflake<br />
              ✅ You can download the complete results using the button below<br />
              ✅ You can now proceed to the next step in your workflow
            </Alert>
            
            {/* Show Snowflake save status */}
            {snowflakeSaveSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Results successfully saved to Snowflake
              </Alert>
            )}
            {snowflakeSaveError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Failed to save to Snowflake: {snowflakeSaveError}
              </Alert>
            )}
            
            {/* Show download button */}
            {status?.output_links?.['results.json'] || status?.output_links?.categorized_keywords ? (
              <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6" gutterBottom>
                  Download Results
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Total keywords: {formattedCount}
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<DownloadIcon />}
                  href={status.output_links['results.json'] || status.output_links.categorized_keywords}
                  download
                  component="a"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download Complete Results
                </Button>
              </Paper>
            ) : null}
          </Box>
        </Grow>
      );
    }

    // Show results with smooth transition
    if (showResults && keywordsJsonContent) {
      return (
        <Grow in={true} timeout={600}>
          <Box>
            <KeywordResultsVisualization
              jsonData={keywordsJsonContent}
              isLoading={false}
              isSavingToSnowflake={isSavingToSnowflake}
              snowflakeSaveSuccess={snowflakeSaveSuccess}
              snowflakeSaveError={snowflakeSaveError}
            />
          </Box>
        </Grow>
      );
    }

    // Show skeleton during loading/transition phases
    if (showSkeleton) {
      return (
        <Fade in={true} timeout={400}>
          <Box>
            <ResultsLoadingSkeleton
              phase={currentPhase as any}
              progress={progress}
              message={message}
              showDownloadPrep={hasResultsUrlRef.current}
            />
          </Box>
        </Fade>
      );
    }

    // Show job status panel only for errors (processing info is now in BatchJobProgressBar)
    if (currentPhase === 'failed' || currentPhase === 'cancelled') {
      return (
        <Fade in={true} timeout={300}>
          <Box>
            <JobStatusPanel 
              status={status} 
              jobId={jobId}
              isSavingToSnowflake={isSavingToSnowflake}
              snowflakeSaveSuccess={snowflakeSaveSuccess}
              snowflakeSaveError={snowflakeSaveError}
            />
          </Box>
        </Fade>
      );
    }

    // Don't show redundant processing alerts since BatchJobProgressBar handles all processing info
    if (currentPhase === 'processing') {
      return null;
    }

    // Default loading state for other phases
    return (
      <Fade in={true} timeout={300}>
        <Box>
          <Alert severity="info" sx={{ m: 2 }}>
            {message}
          </Alert>
        </Box>
      </Fade>
    );
  };

  return (
    <Box key={transitionState.transitionKey}>
      {renderContent()}
    </Box>
  );
};

export default JobStatusTransition;